import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Loader2, Swords, Trophy, Trash2, RefreshCw, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SeatAdminPage = () => {
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState('');
  const [pwErr, setPwErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [duels, setDuels] = useState([]);
  const [form, setForm] = useState({ title: '', fighter_a_name: '', fighter_a_tagline: '', fighter_b_name: '', fighter_b_tagline: '', color_a: '#f59e0b', color_b: '#38bdf8' });
  const [creating, setCreating] = useState(false);

  const load = async (pw) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/seat/duels?password=${encodeURIComponent(pw)}`);
      setDuels(r.data.duels || []); setAuthed(true); setPwErr('');
    } catch (e) {
      if (e.response?.status === 401) { setPwErr('Wrong password'); setAuthed(false); }
      else setPwErr('Failed to load');
    } finally { setLoading(false); }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.title || !form.fighter_a_name || !form.fighter_b_name) { toast.error('Title and both fighter names are required.'); return; }
    setCreating(true);
    toast.loading('AI is inventing superpowers…', { duration: 3000 });
    try {
      await axios.post(`${API}/admin/seat/duels`, { password, ...form });
      toast.success('Duel created with AI-generated powers! 🥊');
      setForm({ title: '', fighter_a_name: '', fighter_a_tagline: '', fighter_b_name: '', fighter_b_tagline: '', color_a: '#f59e0b', color_b: '#38bdf8' });
      load(password);
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); } finally { setCreating(false); }
  };

  const closeDuel = async (slug) => {
    toast.loading('AI referee writing the KO…', { duration: 3000 });
    try { const r = await axios.post(`${API}/admin/seat/duels/${slug}/close?password=${encodeURIComponent(password)}`); toast.success(`KO! ${r.data.ko.winner_name} wins`); load(password); }
    catch (e) { toast.error('Close failed'); }
  };
  const reopen = async (slug) => { try { await axios.post(`${API}/admin/seat/duels/${slug}/reopen?password=${encodeURIComponent(password)}`); load(password); } catch (e) {} };
  const del = async (slug) => { if (!window.confirm('Delete this duel and its votes?')) return; try { await axios.delete(`${API}/admin/seat/duels/${slug}?password=${encodeURIComponent(password)}`); load(password); } catch (e) {} };

  if (!authed) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-5">
        <form onSubmit={(e) => { e.preventDefault(); if (password) load(password); }} className="w-full max-w-sm bg-zinc-900 border border-red-400/20 rounded-2xl p-6" data-testid="seat-admin-login">
          <div className="flex items-center gap-2 mb-4 text-white"><Lock className="w-5 h-5 text-red-400" /><h1 className="font-bold text-xl">Seat Fighter Admin</h1></div>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Admin password" data-testid="seat-admin-password"
            className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-zinc-600 outline-none mb-3" />
          {pwErr && <p className="text-sm text-red-400 mb-3">{pwErr}</p>}
          <button type="submit" disabled={loading} data-testid="seat-admin-login-btn" className="w-full py-3 rounded-xl bg-red-500 text-white font-bold flex items-center justify-center gap-2">{loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Enter'}</button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white px-4 sm:px-6 py-10" data-testid="seat-admin">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-black text-2xl flex items-center gap-2"><Swords className="w-6 h-6 text-red-400" /> Seat Fighter</h1>
          <button onClick={() => load(password)} className="flex items-center gap-2 px-3 py-2 rounded-full bg-zinc-800 text-sm"><RefreshCw className="w-4 h-4" /> Refresh</button>
        </div>

        <form onSubmit={create} className="rounded-2xl bg-zinc-900 border border-white/10 p-5 mb-6 space-y-3" data-testid="seat-create-form">
          <h2 className="font-bold flex items-center gap-2"><Plus className="w-4 h-4 text-red-400" /> New duel (AI invents the powers)</h2>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Duel title (e.g. The Seat of Power)" data-testid="seat-form-title" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-sm outline-none" />
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <input value={form.fighter_a_name} onChange={(e) => setForm({ ...form, fighter_a_name: e.target.value })} placeholder="Fighter A name" data-testid="seat-form-a-name" className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none" />
              <input value={form.fighter_a_tagline} onChange={(e) => setForm({ ...form, fighter_a_tagline: e.target.value })} placeholder="A tagline" className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none" />
              <input type="color" value={form.color_a} onChange={(e) => setForm({ ...form, color_a: e.target.value })} className="w-full h-9 bg-black/40 border border-white/10 rounded-lg" />
            </div>
            <div className="space-y-2">
              <input value={form.fighter_b_name} onChange={(e) => setForm({ ...form, fighter_b_name: e.target.value })} placeholder="Fighter B name" data-testid="seat-form-b-name" className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none" />
              <input value={form.fighter_b_tagline} onChange={(e) => setForm({ ...form, fighter_b_tagline: e.target.value })} placeholder="B tagline" className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none" />
              <input type="color" value={form.color_b} onChange={(e) => setForm({ ...form, color_b: e.target.value })} className="w-full h-9 bg-black/40 border border-white/10 rounded-lg" />
            </div>
          </div>
          <button type="submit" disabled={creating} data-testid="seat-create-btn" className="w-full py-3 rounded-xl bg-gradient-to-r from-red-600 to-orange-500 font-bold flex items-center justify-center gap-2 disabled:opacity-60">{creating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : 'Create duel'}</button>
        </form>

        <div className="space-y-3">
          {duels.map((d) => (
            <div key={d.slug} className="rounded-xl bg-zinc-900 border border-white/10 p-4" data-testid={`seat-admin-duel-${d.slug}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] text-white/50">{d.title} · {d.status}</p>
                  <p className="font-bold"><span style={{ color: d.fighter_a.color }}>{d.fighter_a.name}</span> <span className="text-white/40">vs</span> <span style={{ color: d.fighter_b.color }}>{d.fighter_b.name}</span></p>
                  <p className="text-xs text-white/50 mt-0.5">{d.votes_a} – {d.votes_b} ({d.total_votes} hits)</p>
                  {d.ko && <p className="text-xs text-amber-300 mt-1">🏆 {d.ko.winner_name}: {d.ko.narration}</p>}
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  {d.status === 'active'
                    ? <button onClick={() => closeDuel(d.slug)} className="px-3 py-1.5 rounded-lg bg-amber-500/20 text-amber-300 text-xs font-bold flex items-center gap-1" data-testid={`seat-close-${d.slug}`}><Trophy className="w-3.5 h-3.5" /> Close & KO</button>
                    : <button onClick={() => reopen(d.slug)} className="px-3 py-1.5 rounded-lg bg-emerald-500/20 text-emerald-300 text-xs font-bold">Reopen</button>}
                  <button onClick={() => del(d.slug)} className="px-3 py-1.5 rounded-lg bg-red-500/15 text-red-300 text-xs font-bold flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SeatAdminPage;
